import { useEffect, useRef } from "react"

/**
 * LandingPauseAnimationOnHidden - Speeds up mobile performance
 * -----------------------------
 * Pauses CSS animations inside the referenced element while it is off-screen
 * on very small devices (<500 px). Behaviour is identical to the former
 * `usePauseAnimationOnHidden` hook but namespaced for Landing components.
 */
export function LandingPauseAnimationOnHidden<El extends HTMLElement = HTMLElement>() {
  const ref = useRef<El>(null as unknown as El)

  useEffect(() => {
    // Only apply on very small screens where performance is tight
    const isSmallScreen = window.matchMedia("(max-width: 500px)").matches
    if (!isSmallScreen) return

    const el = ref.current as unknown as El | null
    if (!el) return

    const setPlayState = (state: "paused" | "running") => {
      el.style.animationPlayState = state
      for (const node of el.querySelectorAll<HTMLElement>("*")) {
        node.style.animationPlayState = state
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        setPlayState(entry.isIntersecting ? "running" : "paused")
      },
      { rootMargin: "0px 0px -15% 0px", threshold: 0.01 },
    )

    setPlayState("paused")
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return ref
}
